/**
 * Molecule: Modal
 * Generic modal dialog with header, scrollable body, and footer slot.
 *
 * Set `expandable` to render an expand/collapse toggle in the header — when
 * expanded the modal grows to fill the viewport (full-page detail view).
 *
 * Accessibility: renders as a labelled `role="dialog"` with `aria-modal`,
 * traps Tab focus inside the modal while open, and restores focus to the
 * previously-focused element on close (WAI-ARIA APG dialog pattern). Bottom
 * padding clears the mobile home-indicator via `env(safe-area-inset-bottom)`.
 */
import React, { useEffect, useId, useRef, useState } from 'react'
import { Maximize2, Minimize2, X as XIcon } from 'lucide-react'
import { tokens } from '@/design-system/tokens'

// Elements that can receive keyboard focus, for the Tab trap. Excludes
// disabled/hidden inputs and tabindex="-1" (programmatic-only) targets.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'iframe',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',')

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(el => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement)
}

interface ModalProps {
  open: boolean
  onClose: () => void
  title: React.ReactNode
  children: React.ReactNode
  footer?: React.ReactNode
  width?: string | number
  size?: string  // convenience alias for width
  /** Force an explicit box height (e.g. '90vh') instead of hugging content —
   *  needed when a body child relies on percentage height (an iframe/canvas
   *  filling the body). Leave unset for the default hug-content behavior. */
  height?: string | number
  expandable?: boolean
  defaultExpanded?: boolean
  /** Optional extra header actions rendered next to the expand/close buttons */
  headerActions?: React.ReactNode
  /** Override the body's default padding/layout — e.g. `{ padding: 0 }` for
   *  edge-to-edge content like a PDF/media viewer. Merged over the defaults,
   *  so the safe-area-aware bottom padding still applies unless overridden. */
  bodyStyle?: React.CSSProperties
}

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  width,
  size,
  height,
  expandable = false,
  defaultExpanded = false,
  headerActions,
  bodyStyle,
}) => {
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded)
  const boxRef = useRef<HTMLDivElement>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)
  const titleId = useId()
  useEffect(() => { if (!open) setExpanded(defaultExpanded) }, [open, defaultExpanded])

  // Focus management: on open, remember what was focused and move focus into
  // the dialog; on close/unmount, restore focus to that element. Focus the box
  // itself (tabIndex -1) — rather than the first focusable — so the dialog is
  // announced to screen readers without auto-selecting the Close button or a
  // text field. But if a child already claimed focus (an `autoFocus` input
  // commits before this effect runs), leave it — many consumer modals rely on
  // autoFocus, and stealing it back would regress that.
  useEffect(() => {
    if (!open) return
    previouslyFocusedRef.current = (document.activeElement as HTMLElement | null)
    const box = boxRef.current
    if (box && !box.contains(document.activeElement)) box.focus()
    return () => {
      const prev = previouslyFocusedRef.current
      if (prev && typeof prev.focus === 'function') {
        try { prev.focus() } catch { /* element detached — nothing to restore to */ }
      }
    }
  }, [open])

  // Escape closes; Tab / Shift+Tab cycle focus within the modal (focus trap).
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return }
      if (e.key !== 'Tab') return
      const box = boxRef.current
      if (!box) return
      const active = document.activeElement
      // Stacked modals: if focus lives inside a *different* dialog, that dialog
      // owns the trap — don't yank focus out of the modal on top of us.
      if (active && active !== document.body && !box.contains(active)) {
        const owningDialog = (active as HTMLElement).closest?.('[role="dialog"]')
        if (owningDialog && owningDialog !== box) return
      }
      const focusables = getFocusable(box)
      if (focusables.length === 0) { e.preventDefault(); box.focus(); return }
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey) {
        if (active === first || !box.contains(active)) { e.preventDefault(); last.focus() }
      } else {
        if (active === last || !box.contains(active)) { e.preventDefault(); first.focus() }
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  const computedWidth = width ?? (size === "sm" ? 400 : size === "lg" ? 800 : 560)
  if (!open) return null

  const safeBottom = 'env(safe-area-inset-bottom, 0px)'

  return (
    <>
      <style>{`@keyframes fadeIn { from { opacity:0 } to { opacity:1 } } @keyframes slideUp { from { transform:translateY(16px);opacity:0 } to { transform:translateY(0);opacity:1 } }`}</style>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.5)',
          animation: 'fadeIn 0.15s ease',
          display: 'flex',
          alignItems: typeof window !== 'undefined' && window.innerWidth < 768 ? 'flex-end' : 'center',
          justifyContent: 'center',
        }}
      >
        {/* Modal box */}
        <div
          ref={boxRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          onClick={e => e.stopPropagation()}
          style={{
            width: expanded ? '100vw' : computedWidth,
            maxWidth: expanded ? '100vw' : 'min(90vw, 100vw - 32px)',
            height: expanded ? '100dvh' : height,
            maxHeight: expanded ? '100dvh' : '90dvh',
            background: tokens.color.background.surface,
            borderRadius: expanded ? 0 : (typeof window !== 'undefined' && window.innerWidth < 768 ? '14px 14px 0 0' : tokens.radius.lg),
            border: expanded ? 'none' : `1px solid ${tokens.color.border.default}`,
            display: 'flex', flexDirection: 'column',
            animation: 'slideUp 0.15s ease',
            boxShadow: expanded ? 'none' : '0 20px 60px rgba(0,0,0,0.4)',
            transition: 'width 0.2s ease, max-width 0.2s ease, height 0.2s ease, max-height 0.2s ease, border-radius 0.2s ease',
            // The box is focused programmatically on open (dialog pattern); it
            // is not a keyboard tab-stop, so its focus ring would be noise.
            outline: 'none',
          }}
        >
          {/* Header */}
          <div style={{
            padding: `${tokens.spacing[4]} ${tokens.spacing[5]}`,
            borderBottom: `1px solid ${tokens.color.border.default}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span id={titleId} style={{
              fontSize: 14,
              fontWeight: 600,
              color: tokens.color.text.primary,
              fontFamily: tokens.typography.fontFamily.sans,
              letterSpacing: '-0.005em',
            }}>
              {title}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[2] }}>
              {headerActions}
              {expandable && (
                <button
                  onClick={() => setExpanded(e => !e)}
                  title={expanded ? 'Collapse' : 'Expand to full screen'}
                  aria-label={expanded ? 'Collapse' : 'Expand to full screen'}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: tokens.color.text.muted, padding: tokens.spacing[1],
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: 4,
                  }}
                >
                  {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
              )}
              <button
                onClick={onClose}
                aria-label="Close"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: tokens.color.text.muted, padding: tokens.spacing[1],
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 4,
                }}
              >
                <XIcon size={16} />
              </button>
            </div>
          </div>

          {/* Body — when there is no footer, the body is the bottom-most
              element, so it carries the safe-area inset that clears the mobile
              home-indicator. env() resolves to 0 on desktop → no visual change. */}
          <div style={{
            padding: tokens.spacing[5],
            paddingBottom: footer ? tokens.spacing[5] : `calc(${tokens.spacing[5]} + ${safeBottom})`,
            overflowY: 'auto', flex: 1,
            ...bodyStyle,
          }}>
            {children}
          </div>

          {/* Footer — bottom-most when present; carries the safe-area inset. */}
          {footer && (
            <div style={{
              padding: `${tokens.spacing[4]} ${tokens.spacing[5]} calc(${tokens.spacing[4]} + ${safeBottom})`,
              borderTop: `1px solid ${tokens.color.border.default}`,
              display: 'flex', justifyContent: 'flex-end', gap: tokens.spacing[3],
            }}>
              {footer}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
